import React, { useState } from 'react';
import {
  FiHome as Home,
  FiHelpCircle as HelpCircle,
  FiPhone as Phone,
  FiExternalLink as ExternalLink,
  FiFileText as FileText
} from 'react-icons/fi';
import { FaCar as Car, FaBuilding as Building } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { currentPolicies } from '../utils/policyStatus';
import { AGENCY_PHONE_DISPLAY, AGENCY_PHONE_TEL, AGENCY_EMAIL_MAILTO } from '../constants/contact';

const ClaimsView = () => {
  const { user } = useAuth();
  const [selectedClaimType, setSelectedClaimType] = useState(null);

  const claimTypes = [
    {
      id: 'auto',
      icon: Car,
      title: 'Auto Accident',
      description: 'Report vehicle damage or accident',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'home',
      icon: Home,
      title: 'Home Damage',
      description: 'Property damage, theft, or disaster',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'commercial',
      icon: Building,
      title: 'Business Claim',
      description: 'Commercial property or liability',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'other',
      icon: HelpCircle,
      title: 'Other',
      description: 'Life insurance or other claims',
      color: 'from-gray-500 to-gray-600'
    }
  ];

  const carrierContacts = [
    { name: 'Mercury Insurance', phone: '1-800-503-3724', website: 'mercuryinsurance.com/claims', available: '24/7' },
    { name: 'Progressive', phone: '1-800-776-4737', website: 'progressive.com/claims', available: '24/7' },
    { name: 'Travelers', phone: '1-800-252-4633', website: 'travelers.com/claims', available: '24/7' },
    { name: 'Nationwide', phone: '1-800-421-3535', website: 'nationwide.com/claims', available: '24/7' }
  ];

  const getUserCarriers = () => {
    // Current coverage only: a claims hotline for a policy that ended in 2009
    // sends a client to a carrier that will not help them.
    const userCarriers = [...new Set(currentPolicies(user?.policies).map(p => p.vendor))];
    return carrierContacts.filter(carrier =>
      userCarriers.some(userCarrier =>
        userCarrier.toLowerCase().includes(carrier.name.toLowerCase().split(' ')[0])
      )
    );
  };

  const getPoliciesForCarrier = (carrierName) => {
    return currentPolicies(user?.policies).filter(p =>
      p.vendor.toLowerCase().includes(carrierName.toLowerCase().split(' ')[0])
    );
  };

  const userCarriers = getUserCarriers();

  const handleClaimTypeSelect = (claimType) => {
    setSelectedClaimType(claimType);
  };

  const handleCallCarrier = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  // Claim Type Selection View
  if (selectedClaimType) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSelectedClaimType(null)}
              className="text-[#0038A8] hover:underline"
            >
              ← Back
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {claimTypes.find(t => t.id === selectedClaimType)?.title} Claim
              </h1>
              <p className="text-gray-600">
                Contact your carrier's claims department directly for fastest service
              </p>
            </div>
          </div>
        </div>

        {/* Emergency Notice */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-3">Emergency Situations</h3>
          <p className="text-red-800 mb-4">
            If this is an emergency involving injury or immediate danger, call 911 first, then contact your insurance carrier.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
              🚨 Call 911 First
            </span>
            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
              📱 Then Call Insurance
            </span>
          </div>
        </div>

        {/* Your Carriers */}
        {userCarriers.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Your Insurance Carriers</h2>
            <div className="space-y-4">
              {userCarriers.map((carrier) => {
                const policies = getPoliciesForCarrier(carrier.name);
                return (
                  <div
                    key={carrier.name}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Carrier Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-16 h-10 bg-gray-200 rounded flex items-center justify-center text-xs font-medium text-gray-600">
                            LOGO
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{carrier.name}</h3>
                            <p className="text-gray-600">{carrier.available} Claims Hotline</p>
                          </div>
                        </div>

                        {/* Contact Methods */}
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center gap-3">
                            <Phone size={20} className="text-[#DC143C]" />
                            <div>
                              <div className="font-bold text-xl text-gray-900">{carrier.phone}</div>
                              <div className="text-sm text-gray-600">Available {carrier.available}</div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={() => handleCallCarrier(carrier.phone)}
                            className="bg-[#DC143C] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#B01030] transition-colors flex items-center justify-center gap-2"
                          >
                            <Phone size={20} />
                            Call Now
                          </button>
                          <a
                            href={`https://${carrier.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="border border-[#0038A8] text-[#0038A8] py-3 px-6 rounded-lg font-semibold hover:bg-[#0038A8] hover:text-white transition-colors flex items-center justify-center gap-2"
                          >
                            File Online
                            <ExternalLink size={16} />
                          </a>
                        </div>
                      </div>

                      {/* Your Policies */}
                      <div className="md:w-80 bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Your Policies with {carrier.name}:
                        </h4>
                        <div className="space-y-2">
                          {policies.map((policy) => (
                            <div key={policy.id} className="text-sm">
                              <div className="font-medium text-gray-900">{policy.policyType}</div>
                              <div className="text-gray-600">Policy: {policy.policyNumber}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All Carriers */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">All Carrier Claims Contacts</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {carrierContacts.map((carrier) => (
              <div
                key={carrier.name}
                className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{carrier.name}</h3>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {carrier.available}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="text-gray-600">Phone: </span>
                    <span className="font-medium">{carrier.phone}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Online: </span>
                    <span className="font-medium">{carrier.website}</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleCallCarrier(carrier.phone)}
                    className="flex-1 text-xs bg-[#DC143C] text-white py-2 px-3 rounded font-medium hover:bg-[#B01030] transition-colors"
                  >
                    Call
                  </button>
                  <a
                    href={`https://${carrier.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-xs border border-gray-300 text-gray-700 py-2 px-3 rounded font-medium hover:bg-gray-50 transition-colors text-center"
                  >
                    Online
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Talk to the agency. This portal does not file or track claims --
            the carrier does that, and we help. */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Need help with a claim?</h3>
          <p className="text-gray-600 mb-4">
            Your claim is filed and tracked with your carrier using the numbers above.
            If you would rather we walked you through it, or you are not sure who to
            call, contact us and we will help.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={AGENCY_PHONE_TEL}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Phone size={20} className="text-[#DC143C]" />
              <span className="font-medium">Call or text us: {AGENCY_PHONE_DISPLAY}</span>
            </a>
            <a
              href={AGENCY_EMAIL_MAILTO}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText size={20} className="text-[#0038A8]" />
              <span className="font-medium">Email us</span>
            </a>
          </div>
        </div>

        {/* Claim Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Claim Filing Tips</h3>
          <div className="grid md:grid-cols-2 gap-4 text-blue-800 text-sm">
            <div className="space-y-2">
              <p>• <strong>Document everything:</strong> Take photos of damage</p>
              <p>• <strong>Get a police report:</strong> For auto accidents</p>
              <p>• <strong>Keep receipts:</strong> For temporary repairs or expenses</p>
            </div>
            <div className="space-y-2">
              <p>• <strong>Contact us:</strong> Let us know about your claim</p>
              <p>• <strong>Be honest:</strong> Provide accurate information</p>
              <p>• <strong>Follow up:</strong> Stay in touch with your adjuster</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Claims View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">File a Claim</h1>
        <p className="text-gray-600">
          Select the type of claim you need to file to get connected with the right resources
        </p>
      </div>

      {/* Claim Type Selection */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">What type of claim do you need to file?</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {claimTypes.map((claimType) => (
            <button
              key={claimType.id}
              onClick={() => handleClaimTypeSelect(claimType.id)}
              className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200 card-hover text-left group"
            >
              <div
                className={`w-16 h-16 bg-gradient-to-br ${claimType.color} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200`}
              >
                <claimType.icon size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{claimType.title}</h3>
              <p className="text-gray-600 mb-4">{claimType.description}</p>
              <div className="text-[#DC143C] font-semibold group-hover:underline">
                Get Help →
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Contact */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Need Immediate Assistance?</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="font-semibold text-red-900 mb-2">Emergency</h3>
            <p className="text-red-700 text-sm mb-3">
              For life-threatening emergencies or immediate danger
            </p>
            <a
              href="tel:911"
              className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              <Phone size={16} />
              Call 911
            </a>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">Agent Support</h3>
            <p className="text-blue-700 text-sm mb-3">
              Speak with our team for guidance on your claim
            </p>
            <a
              href={AGENCY_PHONE_TEL}
              className="inline-flex items-center gap-2 bg-[#0038A8] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#002d8a] transition-colors"
            >
              <Phone size={16} />
              {AGENCY_PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>

      {/* General Information */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Before You File a Claim</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-700">
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900">Safety First</h4>
            <p>Ensure everyone is safe and seek medical attention if needed</p>
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900">Document Everything</h4>
            <p>Take photos, get contact information, and keep all receipts</p>
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900">Contact Authorities</h4>
            <p>Call police for accidents and fire department for property damage</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimsView;